import { AnimatePresence, motion, useAnimate, useReducedMotion } from 'motion/react'
import { Eye, EyeOff } from 'lucide-react'
import {
  type CSSProperties,
  type ComponentProps,
  type ComponentType,
  type ReactNode,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react'
import { cn } from '../lib/utils'

/* ------------------------------------------------------------------------ */
/* Media query (mount heavy art only where it is shown)                      */
/* ------------------------------------------------------------------------ */

export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = window.matchMedia(query)
      list.addEventListener('change', onChange)
      return () => list.removeEventListener('change', onChange)
    },
    () => window.matchMedia(query).matches,
    () => false,
  )
}

/* ------------------------------------------------------------------------ */
/* Illustration                                                              */
/* ------------------------------------------------------------------------ */

const card = 'fill-white dark:fill-white/[0.07]'
const cardEdge = 'stroke-white/0 dark:stroke-white/15'
const ink = 'fill-slate-900 dark:fill-white'
const inkSoft = 'fill-slate-500 dark:fill-white/60'
const rule = 'stroke-slate-200 dark:stroke-white/15'

/**
 * The brand-panel illustration: a GST invoice being written, a sales chart
 * rising, a barcode being scanned at the counter and a payment landing.
 * Inline SVG in the brand ramp, so it follows a tenant's palette and theme.
 */
export function AuthIllustration({ className }: { className?: string }) {
  const bars = [38, 56, 44, 70, 62, 88]
  return (
    <svg viewBox="0 0 520 420" className={cn('h-auto w-full select-none', className)} aria-hidden>
      <defs>
        <linearGradient id="auth-bar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--color-brand-400)" />
          <stop offset="1" stopColor="var(--color-brand-600)" />
        </linearGradient>
        <linearGradient id="auth-head" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="var(--color-brand-500)" />
          <stop offset="1" stopColor="var(--color-brand-700)" />
        </linearGradient>
        <filter id="auth-shadow" x="-20%" y="-20%" width="140%" height="150%">
          <feDropShadow dx="0" dy="18" stdDeviation="16" floodColor="#0b1020" floodOpacity="0.35" />
        </filter>
      </defs>

      {/* Invoice */}
      <g className="auth-float" style={{ animationDelay: '-2s' }}>
        <g filter="url(#auth-shadow)">
          <rect x="34" y="44" width="250" height="310" rx="18" className={cn(card, cardEdge)} />
        </g>
        <rect x="34" y="44" width="250" height="56" rx="18" fill="url(#auth-head)" />
        <rect x="34" y="82" width="250" height="18" fill="url(#auth-head)" />
        <text x="56" y="78" className="fill-white" fontSize="15" fontWeight="700" letterSpacing="1.5">
          TAX INVOICE
        </text>
        <text x="262" y="78" className="fill-white/80" fontSize="11" textAnchor="end">
          #INV-2041
        </text>
        {[0, 1, 2, 3].map((i) => (
          <g key={i}>
            <line
              x1="56"
              x2={i % 2 ? 190 : 214}
              y1={134 + i * 34}
              y2={134 + i * 34}
              strokeWidth="8"
              strokeLinecap="round"
              className="auth-draw stroke-slate-200 dark:stroke-white/20"
              style={{ '--len': 170, '--delay': `${0.3 + i * 0.18}s` } as CSSProperties}
            />
            <line
              x1="232"
              x2="262"
              y1={134 + i * 34}
              y2={134 + i * 34}
              strokeWidth="8"
              strokeLinecap="round"
              className="auth-draw"
              stroke="var(--color-brand-400)"
              style={{ '--len': 40, '--delay': `${0.45 + i * 0.18}s` } as CSSProperties}
            />
          </g>
        ))}
        <line x1="56" x2="262" y1="270" y2="270" strokeDasharray="4 6" className={rule} strokeWidth="1.5" />
        <text x="56" y="296" className={inkSoft} fontSize="11">
          CGST 9% + SGST 9%
        </text>
        <text x="56" y="326" className={ink} fontSize="13" fontWeight="600">
          Total
        </text>
        <text x="262" y="327" className={ink} fontSize="19" fontWeight="700" textAnchor="end">
          ₹24,780
        </text>
        <g className="auth-pop" style={{ '--delay': '1.5s' } as CSSProperties}>
          <g transform="rotate(-14 226 300)">
            <rect
              x="190"
              y="282"
              width="72"
              height="30"
              rx="6"
              fill="none"
              strokeWidth="2.5"
              className="stroke-emerald-500"
            />
            <text
              x="226"
              y="302"
              textAnchor="middle"
              fontSize="14"
              fontWeight="800"
              letterSpacing="2"
              className="fill-emerald-500"
            >
              PAID
            </text>
          </g>
        </g>
      </g>

      {/* Sales chart */}
      <g className="auth-float-slow">
        <g filter="url(#auth-shadow)">
          <rect x="304" y="22" width="190" height="150" rx="16" className={cn(card, cardEdge)} />
        </g>
        <text x="322" y="50" className={inkSoft} fontSize="11">
          Sales this week
        </text>
        <text x="322" y="72" className={ink} fontSize="17" fontWeight="700">
          ₹3.2L
        </text>
        <rect x="414" y="58" width="62" height="20" rx="10" className="fill-emerald-500/15" />
        <text
          x="445"
          y="72"
          textAnchor="middle"
          fontSize="11"
          fontWeight="600"
          className="fill-emerald-600 dark:fill-emerald-400"
        >
          +18.4%
        </text>
        {bars.map((h, i) => (
          <rect
            key={i}
            x={324 + i * 26}
            y={156 - h * 0.7}
            width="16"
            height={h * 0.7}
            rx="4"
            fill="url(#auth-bar)"
            className="auth-rise"
            style={{ '--delay': `${0.4 + i * 0.1}s` } as CSSProperties}
          />
        ))}
      </g>

      {/* Counter scan */}
      <g className="auth-float" style={{ animationDelay: '-4s' }}>
        <g filter="url(#auth-shadow)">
          <rect x="300" y="198" width="196" height="138" rx="16" className={cn(card, cardEdge)} />
        </g>
        <text x="318" y="226" className={inkSoft} fontSize="11">
          POS · Counter 1
        </text>
        <g transform="translate(318 238)">
          {[3, 1, 2, 1, 3, 2, 1, 1, 3, 1, 2, 3, 1, 2, 1, 3, 2, 1, 2, 1, 3, 1].map((w, i, all) => {
            const x = all.slice(0, i).reduce((sum, v) => sum + v + 2.6, 0)
            return (
              <rect key={i} x={x} y="0" width={w} height="44" className="fill-slate-800 dark:fill-white/80" />
            )
          })}
          <rect
            x="-6"
            y="2"
            width="104"
            height="3"
            rx="1.5"
            fill="var(--color-brand-500)"
            className="auth-scan"
            style={{ filter: 'drop-shadow(0 0 6px var(--color-brand-400))' }}
          />
        </g>
        <g className="auth-pop" style={{ '--delay': '1.1s' } as CSSProperties}>
          <rect x="318" y="298" width="160" height="26" rx="13" className="fill-emerald-500/15" />
          <circle cx="333" cy="311" r="6" className="fill-emerald-500" />
          <path
            d="M330 311l2.2 2.2 4-4.4"
            stroke="white"
            strokeWidth="1.8"
            fill="none"
            strokeLinecap="round"
          />
          <text
            x="346"
            y="315"
            fontSize="11"
            fontWeight="600"
            className="fill-emerald-700 dark:fill-emerald-300"
          >
            Added · Inverter 1KVA
          </text>
        </g>
      </g>

      {/* Payment chip */}
      <g className="auth-float-slow" style={{ animationDelay: '-3s' }}>
        <g filter="url(#auth-shadow)">
          <rect x="70" y="362" width="208" height="44" rx="22" className={cn(card, cardEdge)} />
        </g>
        <circle cx="96" cy="384" r="6" className="auth-pulse-dot fill-emerald-500" />
        <circle cx="96" cy="384" r="6" className="fill-emerald-500" />
        <text x="112" y="380" fontSize="11" className={inkSoft}>
          UPI received
        </text>
        <text x="112" y="396" fontSize="13" fontWeight="700" className={ink}>
          + ₹12,480
        </text>
      </g>

      {/* GST chip */}
      <g className="auth-float" style={{ animationDelay: '-1s' }}>
        <rect
          x="318"
          y="356"
          width="150"
          height="34"
          rx="17"
          className="fill-white/15 stroke-white/30"
          strokeWidth="1"
        />
        <text x="393" y="378" textAnchor="middle" fontSize="12" fontWeight="600" className="fill-white">
          GSTR-1 ready ✓
        </text>
      </g>
    </svg>
  )
}

/** Compact art for the mobile header band. */
export function AuthMiniArt({ className }: { className?: string }) {
  return (
    <div className={cn('pointer-events-none relative h-16', className)} aria-hidden>
      <div className="auth-float absolute top-1 right-0 flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white ring-1 ring-white/25 backdrop-blur">
        <span className="relative flex size-2">
          <span className="auth-pulse-dot absolute inset-0 rounded-full bg-emerald-400" />
          <span className="relative size-2 rounded-full bg-emerald-400" />
        </span>
        + ₹12,480
      </div>
      <div
        className="auth-float-slow absolute bottom-0 left-0 flex items-end gap-1 rounded-xl bg-white/12 px-2.5 py-2 ring-1 ring-white/20 backdrop-blur"
        style={{ animationDelay: '-2s' }}
      >
        {[10, 16, 12, 20, 18, 26].map((h, i) => (
          <span
            key={i}
            className="auth-rise w-1.5 rounded-sm bg-white/85"
            style={{ height: h, '--delay': `${0.2 + i * 0.08}s` } as CSSProperties}
          />
        ))}
      </div>
      <div
        className="auth-float absolute bottom-1 left-24 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold tracking-wide text-white ring-1 ring-white/25 backdrop-blur"
        style={{ animationDelay: '-3.5s' }}
      >
        GST ✓
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* Rotating feature highlights + trust badges                                */
/* ------------------------------------------------------------------------ */

export interface AuthFeature {
  icon: ComponentType<{ className?: string }>
  title: string
  text?: string
}

export function FeatureRotator({
  features,
  interval = 3800,
}: {
  features: AuthFeature[]
  interval?: number
}) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const reduce = useReducedMotion()

  useEffect(() => {
    if (paused || features.length < 2) return
    const t = setInterval(() => setIndex((i) => (i + 1) % features.length), interval)
    return () => clearInterval(t)
  }, [paused, features.length, interval])

  const current = features[index % Math.max(features.length, 1)]
  if (!current) return null
  const Icon = current.icon

  return (
    <div
      className="grid gap-3"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-live="polite"
    >
      <div className="relative h-[68px] overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={current.title}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -18, filter: 'blur(6px)' }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 flex items-center gap-4"
          >
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-white/12 ring-1 ring-white/25 backdrop-blur">
              <Icon className="size-5.5" />
            </span>
            <span className="min-w-0">
              <span className="block font-semibold">{current.title}</span>
              {current.text && <span className="mt-0.5 block text-sm text-white/70">{current.text}</span>}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex gap-1.5" role="tablist" aria-label="Highlights">
        {features.map((f, i) => (
          <button
            key={f.title}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={f.title}
            onClick={() => setIndex(i)}
            className={cn(
              'h-1.5 cursor-pointer rounded-full transition-all duration-500 focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:outline-none',
              i === index ? 'w-7 bg-white' : 'w-1.5 bg-white/35 hover:bg-white/60',
            )}
          />
        ))}
      </div>
    </div>
  )
}

export function TrustBadges({
  items,
}: {
  items: { icon: ComponentType<{ className?: string }>; label: string }[]
}) {
  return (
    <ul className="flex flex-wrap gap-2">
      {items.map(({ icon: Icon, label }) => (
        <li
          key={label}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-xs text-white/85 ring-1 ring-white/15 backdrop-blur"
        >
          <Icon className="size-3.5" />
          {label}
        </li>
      ))}
    </ul>
  )
}

/* ------------------------------------------------------------------------ */
/* Form helpers                                                              */
/* ------------------------------------------------------------------------ */

/** Shakes its children whenever `trigger` changes to a truthy new value (e.g. an error count). */
export function Shake({
  trigger,
  children,
  className,
}: {
  trigger: unknown
  children: ReactNode
  className?: string
}) {
  const [scope, animate] = useAnimate<HTMLDivElement>()
  const reduce = useReducedMotion()
  useEffect(() => {
    if (!trigger || reduce || !scope.current) return
    void animate(scope.current, { x: [0, -10, 10, -7, 7, -3, 0] }, { duration: 0.45 })
  }, [trigger, reduce, animate, scope])
  return (
    <div ref={scope} className={className}>
      {children}
    </div>
  )
}

/** A password field with a show/hide toggle. Spread `register()` / field props as usual. */
export function PasswordInput({ className, ...props }: Omit<ComponentProps<'input'>, 'type'>) {
  const [visible, setVisible] = useState(false)
  return (
    <div className="relative">
      <input
        {...props}
        type={visible ? 'text' : 'password'}
        className={cn(
          'h-11 w-full min-w-0 rounded-lg border border-input bg-surface px-3 pr-11 text-sm text-foreground shadow-soft',
          'transition-[border-color,box-shadow] duration-150 placeholder:text-muted-foreground/70',
          'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/20 focus-visible:outline-none',
          'aria-invalid:border-danger aria-invalid:ring-danger/15',
          className,
        )}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute inset-y-0 right-0 grid w-11 cursor-pointer place-items-center rounded-r-lg text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
      >
        {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  )
}

/** 0–4. Mirrors the server rule (10+ characters, letters and numbers) and rewards more. */
export function passwordScore(password: string): number {
  if (password.length < 10 || !/[a-z]/i.test(password) || !/\d/.test(password)) {
    return password.length === 0 ? 0 : 1
  }
  let score = 2
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
  if (/[^a-z0-9]/i.test(password) || password.length >= 14) score++
  return Math.min(score, 4)
}

const STRENGTH = [
  { label: '', bar: 'bg-border', text: 'text-muted-foreground' },
  { label: 'Too weak', bar: 'bg-danger', text: 'text-danger' },
  { label: 'Okay', bar: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  { label: 'Good', bar: 'bg-brand-500', text: 'text-brand-600 dark:text-brand-400' },
  { label: 'Strong', bar: 'bg-success', text: 'text-success' },
] as const

export function PasswordStrength({ password, className }: { password: string; className?: string }) {
  const score = passwordScore(password)
  const s = STRENGTH[score] ?? STRENGTH[0]
  return (
    <div className={cn('grid gap-1.5', className)} aria-live="polite">
      <div className="grid grid-cols-4 gap-1.5">
        {[1, 2, 3, 4].map((n) => (
          <span key={n} className="h-1.5 overflow-hidden rounded-full bg-surface-2">
            <span
              className={cn('block h-full rounded-full transition-all duration-500', n <= score ? s.bar : '')}
              style={{ width: n <= score ? '100%' : '0%' }}
            />
          </span>
        ))}
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">10+ characters with letters and numbers</span>
        {s.label && <span className={cn('font-medium', s.text)}>{s.label}</span>}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* Celebration                                                               */
/* ------------------------------------------------------------------------ */

const CONFETTI_COLORS = [
  'var(--color-brand-400)',
  'var(--color-brand-600)',
  'oklch(80% 0.17 85)',
  'oklch(72% 0.19 150)',
  'oklch(70% 0.2 20)',
  'oklch(78% 0.13 220)',
]

/** A one-shot CSS confetti burst over the viewport. Hidden for reduced motion. */
export function Confetti({ pieces = 70 }: { pieces?: number }) {
  return (
    <div className="auth-confetti pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-hidden>
      {Array.from({ length: pieces }, (_, i) => {
        // Deterministic spread (no Math.random in render).
        const r = (n: number): number => ((i * 9301 + n * 49297) % 233280) / 233280
        return (
          <i
            key={i}
            style={
              {
                left: `${r(1) * 100}%`,
                background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                animationDelay: `${r(2) * 0.6}s`,
                animationDuration: `${2.2 + r(3) * 1.6}s`,
                '--dx': `${(r(4) - 0.5) * 240}px`,
                '--rot': `${360 + r(5) * 720}deg`,
                width: `${6 + r(6) * 6}px`,
                height: `${10 + r(7) * 8}px`,
              } as CSSProperties
            }
          />
        )
      })}
    </div>
  )
}
